import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { API_BASE } from "../config/api";

function CommentsDetail() {
    const { videoId } = useParams();

    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [videoData, setVideoData] = useState(null);

    const [replyDrafts, setReplyDrafts] = useState({});
    const [replyModes, setReplyModes] = useState({});
    const [replyingId, setReplyingId] = useState(null);

    const loadVideoComments = async () => {
        try {
            setLoading(true);
            setErrorMessage("");

            const response = await fetch(`${API_BASE}/api/admin/video-comments/video/${videoId}`, {
                method: "GET",
                credentials: "include",
            });

            const data = await response.json();

            if (!response.ok || !data?.success) {
                throw new Error(data?.message || "No fue posible cargar el detalle de comentarios");
            }

            setVideoData(data?.data || null);

            const initialModes = {};
            (data?.data?.comentarios || []).forEach((comment) => {
                initialModes[comment.Comentario_ID] = "admin";
            });
            setReplyModes(initialModes);
        } catch (error) {
            console.error("Error cargando detalle de comentarios:", error);
            setErrorMessage(error.message || "Ocurrió un error al cargar el detalle");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadVideoComments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoId]);

    const handleChangeReply = (comentarioId, value) => {
        setReplyDrafts((prev) => ({
            ...prev,
            [comentarioId]: value,
        }));
    };

    const handleChangeReplyMode = (comentarioId, value) => {
        setReplyModes((prev) => ({
            ...prev,
            [comentarioId]: value,
        }));
    };

    const handleReply = async (comentarioId) => {
        const respuesta = String(replyDrafts[comentarioId] || "").trim();
        const responderComo = String(replyModes[comentarioId] || "admin").trim();

        if (!respuesta) return;

        try {
            setReplyingId(comentarioId);

            const response = await fetch(`${API_BASE}/api/admin/video-comments/${comentarioId}/reply`, {
                method: "POST",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    respuesta,
                    responderComo,
                }),
            });

            const data = await response.json();

            if (!response.ok || !data?.success) {
                throw new Error(data?.message || "No fue posible responder el comentario");
            }

            setReplyDrafts((prev) => ({
                ...prev,
                [comentarioId]: "",
            }));

            await loadVideoComments();
        } catch (error) {
            console.error("Error respondiendo comentario:", error);
            alert(error.message || "Ocurrió un error al responder");
        } finally {
            setReplyingId(null);
        }
    };

    const totalPendientes = useMemo(() => {
        return (videoData?.comentarios || []).reduce(
            (acc, item) => acc + Number(item?.pendienteRespuesta || 0),
            0
        );
    }, [videoData]);

    const formatMexicoDateTime = (value) => {
        if (!value) return "";

        try {
            const date = new Date(value);

            const parts = new Intl.DateTimeFormat("sv-SE", {
                timeZone: "America/Mexico_City",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
            }).formatToParts(date);

            const get = (type) => parts.find((p) => p.type === type)?.value || "";

            return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")}`;
        } catch (error) {
            return value;
        }
    };

    return (
        <>
            <Sidebar />
            <main className="main-content">
                <Header />

                <div className="content-inner container-fluid pb-0" id="page_layout">
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                        <div className="d-flex flex-column">
                            <h3>Detalle de Comentarios</h3>
                            <p className="text-primary mb-0">Panduit Core</p>
                            <small className="text-muted">
                                Pendientes: {totalPendientes}
                            </small>
                        </div>

                        <div>
                            <Link to="/listado-comentarios" className="btn btn-outline-primary">
                                Volver a la lista
                            </Link>
                        </div>
                    </div>

                    {loading ? (
                        <div className="card">
                            <div className="col-md-12 col-sm-4 mb-4">
                                <div className="iq-loader-box">
                                    <div className="iq-loader-1"></div>
                                </div>
                            </div>
                            <div className="card-body text-center">Cargando detalle...</div>
                        </div>
                    ) : errorMessage ? (
                        <div className="alert alert-danger">{errorMessage}</div>
                    ) : !videoData ? (
                        <div className="alert alert-light border">No se encontró información del video.</div>
                    ) : (
                        <>
                            <div className="card mb-4">
                                <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
                                    <div>
                                        <h4 className="mb-1">{videoData.Video_Titulo}</h4>
                                        <small className="text-muted">
                                            Comentarios: {videoData.totalComentarios}
                                        </small>
                                    </div>

                                    <div>
                                        {videoData?.Instructor ? (
                                            <div className="d-flex align-items-center gap-2">
                                                {videoData.Instructor.Instructor_Foto ? (
                                                    <img
                                                        src={videoData.Instructor.Instructor_Foto}
                                                        alt={videoData.Instructor.Instructor_Nombre}
                                                        style={{
                                                            width: "44px",
                                                            height: "44px",
                                                            borderRadius: "50%",
                                                            objectFit: "cover",
                                                        }}
                                                    />
                                                ) : (
                                                    <div
                                                        style={{
                                                            width: "44px",
                                                            height: "44px",
                                                            borderRadius: "50%",
                                                            background: "#e9ecef",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontWeight: "bold",
                                                        }}
                                                    >
                                                        {(videoData.Instructor.Instructor_Nombre || "I").charAt(0)}
                                                    </div>
                                                )}

                                                <div className="d-flex flex-column">
                                                    <strong>{videoData.Instructor.Instructor_Nombre}</strong>
                                                    <small className="text-muted">Instructor asignado</small>
                                                </div>
                                            </div>
                                        ) : (
                                            <span className="text-muted">Sin instructor asignado</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {videoData.comentarios?.length === 0 ? (
                                <div className="alert alert-light border">
                                    Este video aún no tiene comentarios.
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-4">
                                    {videoData.comentarios.map((comment) => (
                                        <div
                                            key={comment.Comentario_ID}
                                            className="card"
                                        >
                                            <div className="card-body">
                                                <div className="d-flex gap-3 align-items-start">
                                                    <div>
                                                        {comment?.Usuario?.Foto_URL ? (
                                                            <img
                                                                src={comment.Usuario.Foto_URL}
                                                                alt={comment?.Usuario?.Nombre_Completo || "Usuario"}
                                                                style={{
                                                                    width: "56px",
                                                                    height: "56px",
                                                                    borderRadius: "50%",
                                                                    objectFit: "cover",
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    width: "56px",
                                                                    height: "56px",
                                                                    borderRadius: "50%",
                                                                    background: "#e9ecef",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    fontWeight: "bold",
                                                                }}
                                                            >
                                                                {(comment?.Usuario?.Nombre_Completo || "U").charAt(0)}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex-grow-1">
                                                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                                                            <div>
                                                                <strong>{comment?.Usuario?.Nombre_Completo || "Usuario"}</strong>
                                                                <div className="text-muted" style={{ fontSize: "13px" }}>
                                                                    {formatMexicoDateTime(comment?.Comentario_Act)}
                                                                </div>
                                                            </div>

                                                            <div>
                                                                {comment.pendienteRespuesta ? (
                                                                    <span className="badge bg-warning text-dark">
                                                                        Pendiente
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-success">
                                                                        Respondido
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <p className="mb-3">{comment?.Comentario_Texto}</p>

                                                        {Array.isArray(comment?.respuestas) && comment.respuestas.length > 0 && (
                                                            <div className="d-flex flex-column gap-3 mb-4">
                                                                {comment.respuestas.map((respuesta) => (
                                                                    <div
                                                                        key={respuesta.RespuestaComentario_ID}
                                                                        className="p-3 rounded"
                                                                        style={{
                                                                            background: "#f8f9fa",
                                                                            borderLeft: "4px solid #0d6efd",
                                                                        }}
                                                                    >
                                                                        <div className="d-flex gap-3 align-items-start">
                                                                            <div>
                                                                                {respuesta?.Instructor?.Instructor_Foto ? (
                                                                                    <img
                                                                                        src={respuesta.Instructor.Instructor_Foto}
                                                                                        alt={respuesta?.Autor_Nombre || "Autor"}
                                                                                        style={{
                                                                                            width: "44px",
                                                                                            height: "44px",
                                                                                            borderRadius: "50%",
                                                                                            objectFit: "cover",
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <div
                                                                                        style={{
                                                                                            width: "44px",
                                                                                            height: "44px",
                                                                                            borderRadius: "50%",
                                                                                            background: "#dbeafe",
                                                                                            display: "flex",
                                                                                            alignItems: "center",
                                                                                            justifyContent: "center",
                                                                                            fontWeight: "bold",
                                                                                        }}
                                                                                    >
                                                                                        {(respuesta?.Autor_Nombre || "A").charAt(0)}
                                                                                    </div>
                                                                                )}
                                                                            </div>

                                                                            <div className="flex-grow-1">
                                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                                    <strong>{respuesta.Autor_Nombre}</strong>
                                                                                </div>

                                                                                <small className="text-muted d-block mb-2">
                                                                                    {formatMexicoDateTime(respuesta?.Respuesta_Act)}
                                                                                </small>

                                                                                <p className="mb-0">{respuesta.Respuesta_Texto}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        <div className="row g-3">
                                                            <div className="col-md-3">
                                                                <label className="form-label">Responder como</label>
                                                                <select
                                                                    className="form-select"
                                                                    value={replyModes[comment.Comentario_ID] || "admin"}
                                                                    onChange={(e) =>
                                                                        handleChangeReplyMode(comment.Comentario_ID, e.target.value)
                                                                    }
                                                                >
                                                                    <option value="admin">Administrador</option>
                                                                    <option
                                                                        value="instructor"
                                                                        disabled={!videoData?.Instructor?.Instructor_ID}
                                                                    >
                                                                        Instructor asignado
                                                                    </option>
                                                                </select>
                                                            </div>

                                                            <div className="col-md-9">
                                                                <label className="form-label">Respuesta</label>
                                                                <textarea
                                                                    className="form-control"
                                                                    rows={3}
                                                                    placeholder="Escribe una respuesta..."
                                                                    value={replyDrafts[comment.Comentario_ID] || ""}
                                                                    onChange={(e) =>
                                                                        handleChangeReply(comment.Comentario_ID, e.target.value)
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="d-flex justify-content-end mt-3">
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary"
                                                                onClick={() => handleReply(comment.Comentario_ID)}
                                                                disabled={
                                                                    replyingId === comment.Comentario_ID ||
                                                                    !String(replyDrafts[comment.Comentario_ID] || "").trim()
                                                                }
                                                            >
                                                                {replyingId === comment.Comentario_ID
                                                                    ? "Respondiendo..."
                                                                    : "Responder"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </main>
        </>
    );
}

export default CommentsDetail;